import { create } from 'zustand'
import { terrariumApi } from '../services/terrariumApi'
import useSettingsStore from './settingsStore'

// Available types
export const PLANT_TYPES = [
  'flower', 'mushroom', 'cactus', 'sunflower', 'tulip', 'rose',
  'tree', 'fern', 'succulent', 'bamboo', 'vine', 'bonsai'
]

// Legendary types (unlocked via bits)
export const LEGENDARY_PLANT_TYPES = ['cherry_blossom', 'crystal_tree', 'venus_flytrap']
export const LEGENDARY_INSECT_TYPES = ['moth', 'mantis', 'scorpion']

export const ALL_PLANT_TYPES = [...PLANT_TYPES, ...LEGENDARY_PLANT_TYPES]

export const INSECT_TYPES = [
  'ladybug', 'butterfly', 'bee', 'caterpillar',
  'firefly', 'beetle', 'dragonfly', 'snail', 'worm'
]

export const ALL_INSECT_TYPES = [...INSECT_TYPES, ...LEGENDARY_INSECT_TYPES]

// Flying vs crawling insects
export const FLYING_INSECTS = ['butterfly', 'bee', 'firefly', 'dragonfly', 'moth']
const CRAWLING_INSECTS = ['ladybug', 'caterpillar', 'beetle', 'snail', 'worm', 'mantis', 'scorpion']

const getSettings = () => useSettingsStore.getState().settings

// Assign a soil position for a plant based on existing plants
// Each plant center must be at least PLANT_WIDTH apart from every other
function assignSoilPosition(existingPlants) {
  const PLANT_WIDTH = 12 // minimum distance between plant centers
  const MIN_X = 9       // leftmost allowed center
  const MAX_X = 91      // rightmost allowed center

  const takenPositions = Object.values(existingPlants).map(p => p.position.x).sort((a, b) => a - b)

  if (takenPositions.length === 0) {
    return { x: MIN_X + Math.random() * (MAX_X - MIN_X), y: 0 }
  }

  // Build list of valid placement ranges (center positions where new plant fits)
  const ranges = []

  // Range before first plant
  const beforeEnd = takenPositions[0] - PLANT_WIDTH
  if (beforeEnd >= MIN_X) {
    ranges.push({ start: MIN_X, end: beforeEnd })
  }

  // Ranges between adjacent plants
  for (let i = 0; i < takenPositions.length - 1; i++) {
    const rangeStart = takenPositions[i] + PLANT_WIDTH
    const rangeEnd = takenPositions[i + 1] - PLANT_WIDTH
    if (rangeEnd >= rangeStart) {
      ranges.push({ start: rangeStart, end: rangeEnd })
    }
  }

  // Range after last plant
  const afterStart = takenPositions[takenPositions.length - 1] + PLANT_WIDTH
  if (afterStart <= MAX_X) {
    ranges.push({ start: afterStart, end: MAX_X })
  }

  if (ranges.length === 0) {
    // No room — evenly distribute all plants including the new one
    const count = takenPositions.length + 1
    const spacing = (MAX_X - MIN_X) / (count - 1 || 1)
    const newIndex = Math.floor(Math.random() * count)
    return { x: MIN_X + spacing * newIndex, y: 0 }
  }

  // Pick the largest range for best spacing
  ranges.sort((a, b) => (b.end - b.start) - (a.end - a.start))
  const range = ranges[0]
  // Place randomly within the valid range
  const x = range.start + Math.random() * (range.end - range.start)
  return { x, y: 0 }
}

// Find a non-overlapping position for an insect
function findInsectPosition(existingInsects, insectType) {
  const settings = getSettings()
  const minX = settings.minX || 5
  const maxX = settings.maxX || 95
  const COLLISION_DISTANCE = 6

  // Ground insects stay near the soil, flying insects go higher
  const isGround = CRAWLING_INSECTS.includes(insectType)
  const minY = isGround ? 5 : 25
  const maxY = isGround ? 15 : 55

  const existing = Object.values(existingInsects)

  for (let attempt = 0; attempt < 20; attempt++) {
    const x = minX + Math.random() * (maxX - minX)
    const y = minY + Math.random() * (maxY - minY)
    const tooClose = existing.some(e => {
      const dx = e.position.x - x
      const dy = e.position.y - y
      return Math.sqrt(dx * dx + dy * dy) < COLLISION_DISTANCE
    })
    if (!tooClose) return { x, y }
  }

  return {
    x: minX + Math.random() * (maxX - minX),
    y: minY + Math.random() * (maxY - minY)
  }
}

export const useTerrariumStore = create((set, get) => ({
  // Entity maps
  plants: {},    // { [userId]: PlantEntity }
  insects: {},   // { [userId]: InsectEntity }

  // Intervals
  roamingInterval: null,
  updateInterval: null,
  refreshInterval: null,

  // Context
  broadcasterLogin: null,
  lastAction: null,

  // ============================================
  // SETUP
  // ============================================

  setBroadcasterLogin: (login) => {
    terrariumApi.setBroadcasterLogin(login)
    set({ broadcasterLogin: login })
  },

  // ============================================
  // LOAD FROM API
  // ============================================

  loadFromApi: async (broadcasterLogin) => {
    try {
      terrariumApi.setBroadcasterLogin(broadcasterLogin)
      const { plants: plantList, insects: insectList } = await terrariumApi.loadAll()

      const plantsMap = {}
      const tempPlants = {}

      // Build plants map with soil positions
      for (const p of plantList) {
        const pos = assignSoilPosition(tempPlants)
        const plantEntity = {
          userId: p.userId,
          username: p.username,
          plantType: p.plantType,
          growthStage: p.growthStage,
          health: p.health,
          plantedAt: p.plantedAt,
          lastWateredAt: p.lastWateredAt,
          isAlive: p.isAlive !== false,
          position: { x: parseFloat(p.positionX) || pos.x, y: 0 },
          variant: p.variant || 'default',
          isWatering: false,
          isBlooming: false,
          isWaving: false,
          isShaking: false,
          isGlowing: false,
          spawning: false,
          despawning: false,
          chatMessage: null
        }
        plantsMap[p.userId] = plantEntity
        tempPlants[p.userId] = plantEntity
      }

      // Build insects map with positions
      const insectsMap = {}
      const tempInsects = {}
      for (const i of insectList) {
        const pos = findInsectPosition(tempInsects, i.insectType)
        const insectEntity = {
          userId: i.userId,
          username: i.username,
          insectType: i.insectType,
          canFly: FLYING_INSECTS.includes(i.insectType),
          position: pos,
          targetX: pos.x,
          targetY: pos.y,
          facingLeft: Math.random() > 0.5,
          variant: i.variant || 'default',
          isWalking: false,
          isFlying: false,
          isLanded: false,
          landedOnUserId: null,
          isPollinatingUserId: null,
          isVisiting: false,
          isDancing: false,
          isPetted: false,
          isSinging: false,
          isNuzzling: false,
          chatMessage: null,
          spawning: false,
          despawning: false,
          lastActivity: Date.now()
        }
        insectsMap[i.userId] = insectEntity
        tempInsects[i.userId] = insectEntity
      }

      set({
        plants: plantsMap,
        insects: insectsMap,
        broadcasterLogin
      })

      // Start roaming if there are insects
      if (Object.keys(insectsMap).length > 0) {
        get().startRoaming()
      }

      // Start growth polling
      get().startRefreshPolling()

      console.log(`Loaded ${plantList.length} plants and ${insectList.length} insects`)
    } catch (error) {
      console.error('Failed to load terrarium entities:', error)
      set({ plants: {}, insects: {}, broadcasterLogin })
    }
  },

  // ============================================
  // ENTITY HELPERS
  // ============================================

  hasEntity: (userId) => {
    const { plants, insects } = get()
    return !!plants[userId] || !!insects[userId]
  },

  getEntity: (userId) => {
    const { plants, insects } = get()
    if (plants[userId]) return { type: 'plant', entity: plants[userId] }
    if (insects[userId]) return { type: 'insect', entity: insects[userId] }
    return null
  },

  getEntityByUsername: (username) => {
    const { plants, insects } = get()
    const lowerName = username.toLowerCase()
    for (const p of Object.values(plants)) {
      if (p.username.toLowerCase() === lowerName) return { type: 'plant', entity: p }
    }
    for (const i of Object.values(insects)) {
      if (i.username.toLowerCase() === lowerName) return { type: 'insect', entity: i }
    }
    return null
  },

  getPlantByUsername: (username) => {
    const { plants } = get()
    const lowerName = username.toLowerCase()
    for (const [userId, p] of Object.entries(plants)) {
      if (p.username.toLowerCase() === lowerName) return { userId, ...p }
    }
    return null
  },

  getInsectByUsername: (username) => {
    const { insects } = get()
    const lowerName = username.toLowerCase()
    for (const [userId, i] of Object.entries(insects)) {
      if (i.username.toLowerCase() === lowerName) return { userId, ...i }
    }
    return null
  },

  // ============================================
  // PLANT ACTIONS
  // ============================================

  addPlant: async (userId, username, plantType = 'flower') => {
    const { plants } = get()
    const settings = getSettings()

    if (plants[userId]) {
      return { success: false, error: 'entity_exists' }
    }

    const type = ALL_PLANT_TYPES.includes(plantType) ? plantType : settings.defaultPlantType || 'flower'

    const maxPlants = settings.maxPlants || 50
    if (Object.keys(plants).length >= maxPlants) {
      return { success: false, error: 'max_plants' }
    }

    const saved = await terrariumApi.addPlant(userId, username, type)
    if (!saved) {
      return { success: false, error: 'api_error' }
    }

    const pos = assignSoilPosition(plants)
    const newPlant = {
      userId,
      username,
      plantType: type,
      growthStage: 0,
      health: 100,
      plantedAt: new Date().toISOString(),
      lastWateredAt: new Date().toISOString(),
      isAlive: true,
      position: { x: parseFloat(saved.positionX) || pos.x, y: 0 },
      isWatering: false,
      isBlooming: false,
      isWaving: false,
      isShaking: false,
      isGlowing: false,
      spawning: true,
      despawning: false,
      chatMessage: null
    }

    set(state => ({
      plants: { ...state.plants, [userId]: newPlant },
      lastAction: { action: 'planted', username, plantType: type, timestamp: new Date().toISOString() }
    }))

    setTimeout(() => {
      set(state => {
        if (!state.plants[userId]) return state
        return {
          plants: {
            ...state.plants,
            [userId]: { ...state.plants[userId], spawning: false }
          }
        }
      })
    }, settings.spawnDuration || 800)

    return { success: true, plant: newPlant }
  },

  removePlant: async (userId) => {
    const { plants } = get()
    if (!plants[userId]) return false
    const settings = getSettings()

    // Start despawn animation
    set(state => ({
      plants: {
        ...state.plants,
        [userId]: { ...state.plants[userId], despawning: true }
      }
    }))

    await terrariumApi.removePlant(userId)

    setTimeout(() => {
      set(state => {
        const newPlants = { ...state.plants }
        delete newPlants[userId]
        return { plants: newPlants }
      })
    }, settings.despawnDuration || 500)

    return true
  },

  removePlantByUsername: async (username) => {
    const plant = get().getPlantByUsername(username)
    if (!plant) return false
    return get().removePlant(plant.userId)
  },

  waterPlant: async (targetUserId, actorUserId, actorUsername) => {
    const { plants } = get()
    const target = plants[targetUserId]
    if (!target) return { success: false, error: 'no_plant' }
    const settings = getSettings()

    // Show watering animation
    set(state => ({
      plants: {
        ...state.plants,
        [targetUserId]: { ...state.plants[targetUserId], isWatering: true }
      },
      lastAction: {
        action: 'watered',
        username: actorUsername,
        targetUsername: target.username,
        timestamp: new Date().toISOString()
      }
    }))

    const updated = await terrariumApi.waterPlant(targetUserId, actorUserId, actorUsername)
    if (updated) {
      set(state => {
        if (!state.plants[targetUserId]) return state
        return {
          plants: {
            ...state.plants,
            [targetUserId]: {
              ...state.plants[targetUserId],
              health: updated.health,
              lastWateredAt: updated.lastWateredAt,
              growthStage: updated.growthStage
            }
          }
        }
      })
    }

    setTimeout(() => {
      set(state => {
        if (!state.plants[targetUserId]) return state
        return {
          plants: {
            ...state.plants,
            [targetUserId]: { ...state.plants[targetUserId], isWatering: false }
          }
        }
      })
    }, settings.waterAnimationDuration || 2000)

    return { success: true }
  },

  bloomPlant: (userId, force = false) => {
    const { plants } = get()
    if (!plants[userId]) return
    if (!force && plants[userId].growthStage < 100) return

    // Force bloom: boost to 100% growth instantly
    const growthStage = force ? 100 : plants[userId].growthStage

    set(state => ({
      plants: {
        ...state.plants,
        [userId]: { ...state.plants[userId], isBlooming: true, growthStage }
      }
    }))

    setTimeout(() => {
      set(state => {
        if (!state.plants[userId]) return state
        return {
          plants: {
            ...state.plants,
            [userId]: { ...state.plants[userId], isBlooming: false }
          }
        }
      })
    }, 3000)
  },

  // ============================================
  // INSECT ACTIONS
  // ============================================

  addInsect: async (userId, username, insectType = 'ladybug') => {
    const { insects } = get()
    const settings = getSettings()

    if (insects[userId]) {
      return { success: false, error: 'entity_exists' }
    }

    const type = ALL_INSECT_TYPES.includes(insectType) ? insectType : settings.defaultInsectType || 'ladybug'

    const maxInsects = settings.maxInsects || 30
    if (Object.keys(insects).length >= maxInsects) {
      return { success: false, error: 'max_insects' }
    }

    const saved = await terrariumApi.addInsect(userId, username, type)
    if (!saved) {
      return { success: false, error: 'api_error' }
    }

    const pos = findInsectPosition(insects, type)
    const newInsect = {
      userId,
      username,
      insectType: type,
      canFly: FLYING_INSECTS.includes(type),
      position: pos,
      targetX: pos.x,
      targetY: pos.y,
      facingLeft: Math.random() > 0.5,
      isWalking: false,
      isFlying: false,
      isLanded: false,
      landedOnUserId: null,
      isPollinatingUserId: null,
      isVisiting: false,
      isDancing: false,
      isPetted: false,
      isSinging: false,
      isNuzzling: false,
      chatMessage: null,
      spawning: true,
      despawning: false,
      lastActivity: Date.now()
    }

    set(state => ({
      insects: { ...state.insects, [userId]: newInsect },
      lastAction: { action: 'joined', username, insectType: type, timestamp: new Date().toISOString() }
    }))

    // Start roaming if this is the first insect
    if (Object.keys(insects).length === 0) {
      get().startRoaming()
    }

    setTimeout(() => {
      set(state => {
        if (!state.insects[userId]) return state
        return {
          insects: {
            ...state.insects,
            [userId]: { ...state.insects[userId], spawning: false }
          }
        }
      })
    }, settings.spawnDuration || 800)

    return { success: true, insect: newInsect }
  },

  removeInsect: async (userId) => {
    const { insects } = get()
    if (!insects[userId]) return false
    const settings = getSettings()

    set(state => ({
      insects: {
        ...state.insects,
        [userId]: { ...state.insects[userId], despawning: true }
      }
    }))

    await terrariumApi.removeInsect(userId)

    setTimeout(() => {
      set(state => {
        const newInsects = { ...state.insects }
        delete newInsects[userId]
        // Stop roaming if no insects left
        if (Object.keys(newInsects).length === 0) {
          get().stopRoaming()
        }
        return { insects: newInsects }
      })
    }, settings.despawnDuration || 500)

    return true
  },

  removeInsectByUsername: async (username) => {
    const insect = get().getInsectByUsername(username)
    if (!insect) return false
    return get().removeInsect(insect.userId)
  },

  // Insect: fly to a new random spot
  flyInsect: (userId) => {
    const { insects } = get()
    const insect = insects[userId]
    if (!insect || insect.despawning) return

    const settings = getSettings()
    const minX = settings.minX || 5
    const maxX = settings.maxX || 95
    const isGround = !insect.canFly
    const minY = isGround ? 5 : 25
    const maxY = isGround ? 15 : 55

    set(state => ({
      insects: {
        ...state.insects,
        [userId]: {
          ...state.insects[userId],
          targetX: minX + Math.random() * (maxX - minX),
          targetY: minY + Math.random() * (maxY - minY),
          isLanded: false,
          landedOnUserId: null,
          isFlying: insect.canFly,
          isWalking: !insect.canFly
        }
      }
    }))
  },

  // Insect: land on a plant
  landInsect: (insectUserId, plantUserId) => {
    const { insects, plants } = get()
    const insect = insects[insectUserId]
    const plant = plants[plantUserId]
    if (!insect || !plant) return

    const landX = plant.position.x + (Math.random() * 6 - 3)
    const landY = insect.canFly ? (20 + Math.random() * 5) : (5 + Math.random() * 10)

    set(state => ({
      insects: {
        ...state.insects,
        [insectUserId]: {
          ...state.insects[insectUserId],
          position: { x: landX, y: landY },
          targetX: landX,
          targetY: landY,
          isLanded: true,
          landedOnUserId: plantUserId,
          isFlying: false,
          isWalking: false
        }
      }
    }))

    // Unlatch after 8 seconds
    setTimeout(() => {
      set(state => {
        if (!state.insects[insectUserId]) return state
        return {
          insects: {
            ...state.insects,
            [insectUserId]: {
              ...state.insects[insectUserId],
              isLanded: false,
              landedOnUserId: null
            }
          }
        }
      })
    }, 8000)
  },

  // Insect: pollinate a plant (helps it grow)
  pollinateInsect: async (insectUserId, plantUserId) => {
    const { insects, plants } = get()
    const insect = insects[insectUserId]
    const plant = plants[plantUserId]
    if (!insect || !plant) return { success: false }

    // Move insect to plant immediately
    const pollinateX = plant.position.x + (Math.random() * 4 - 2)
    const pollinateY = 18

    set(state => ({
      insects: {
        ...state.insects,
        [insectUserId]: {
          ...state.insects[insectUserId],
          position: { x: pollinateX, y: pollinateY },
          targetX: pollinateX,
          targetY: pollinateY,
          isLanded: true,
          landedOnUserId: plantUserId,
          isPollinatingUserId: plantUserId,
          isFlying: false
        }
      },
      lastAction: {
        action: 'pollinated',
        username: insect.username,
        targetUsername: plant.username,
        timestamp: new Date().toISOString()
      }
    }))

    // Trigger a water/grow on the target plant via API
    const updated = await terrariumApi.waterPlant(plantUserId, insectUserId, insect.username)
    if (updated) {
      set(state => {
        if (!state.plants[plantUserId]) return state
        return {
          plants: {
            ...state.plants,
            [plantUserId]: {
              ...state.plants[plantUserId],
              health: updated.health,
              growthStage: updated.growthStage,
              lastWateredAt: updated.lastWateredAt
            }
          }
        }
      })
    }

    // Release after 4 seconds
    setTimeout(() => {
      set(state => {
        if (!state.insects[insectUserId]) return state
        return {
          insects: {
            ...state.insects,
            [insectUserId]: {
              ...state.insects[insectUserId],
              isLanded: false,
              landedOnUserId: null,
              isPollinatingUserId: null
            }
          }
        }
      })
    }, 4000)

    return { success: true }
  },

  // Cross-entity visit
  visitEntity: (visitorUserId, targetUsername) => {
    const { plants, insects } = get()
    const visitor = get().getEntity(visitorUserId)
    const target = get().getEntityByUsername(targetUsername)
    if (!visitor || !target) return

    if (visitor.type === 'insect') {
      const visitorInsect = insects[visitorUserId]
      const visitX = target.entity.position.x + (Math.random() * 6 - 3)
      const visitY = !visitorInsect.canFly ? (5 + Math.random() * 10) : (target.type === 'plant' ? 22 : target.entity.position.y)

      // Insect visits any entity - move to it immediately
      set(state => ({
        insects: {
          ...state.insects,
          [visitorUserId]: {
            ...state.insects[visitorUserId],
            position: { x: visitX, y: visitY },
            targetX: visitX,
            targetY: visitY,
            isVisiting: true,
            isFlying: false,
            isWalking: false
          }
        }
      }))

      // Plant waves
      const targetUserId = Object.entries(plants).find(
        ([, p]) => p.username.toLowerCase() === targetUsername.toLowerCase()
      )?.[0]
      if (targetUserId) {
        set(state => ({
          plants: {
            ...state.plants,
            [targetUserId]: { ...state.plants[targetUserId], isWaving: true }
          }
        }))

        setTimeout(() => {
          set(state => {
            if (!state.plants[targetUserId]) return state
            return {
              plants: {
                ...state.plants,
                [targetUserId]: { ...state.plants[targetUserId], isWaving: false }
              }
            }
          })
        }, 3000)
      }

      setTimeout(() => {
        set(state => {
          if (!state.insects[visitorUserId]) return state
          return {
            insects: {
              ...state.insects,
              [visitorUserId]: { ...state.insects[visitorUserId], isVisiting: false }
            }
          }
        })
      }, 4000)
    }
  },

  // ============================================
  // FUN INTERACTIONS
  // ============================================

  danceInsect: (userId) => {
    const { insects } = get()
    if (!insects[userId] || insects[userId].despawning) return
    set(state => ({
      insects: { ...state.insects, [userId]: { ...state.insects[userId], isDancing: true } }
    }))
    setTimeout(() => {
      set(state => {
        if (!state.insects[userId]) return state
        return { insects: { ...state.insects, [userId]: { ...state.insects[userId], isDancing: false } } }
      })
    }, 4000)
  },

  petInsect: (userId) => {
    const { insects } = get()
    if (!insects[userId] || insects[userId].despawning) return
    set(state => ({
      insects: { ...state.insects, [userId]: { ...state.insects[userId], isPetted: true } }
    }))
    setTimeout(() => {
      set(state => {
        if (!state.insects[userId]) return state
        return { insects: { ...state.insects, [userId]: { ...state.insects[userId], isPetted: false } } }
      })
    }, 3000)
  },

  singInsect: (userId) => {
    const { insects } = get()
    if (!insects[userId] || insects[userId].despawning) return
    set(state => ({
      insects: { ...state.insects, [userId]: { ...state.insects[userId], isSinging: true } }
    }))
    setTimeout(() => {
      set(state => {
        if (!state.insects[userId]) return state
        return { insects: { ...state.insects, [userId]: { ...state.insects[userId], isSinging: false } } }
      })
    }, 3500)
  },

  nuzzleEntity: (userId, targetUsername) => {
    const target = get().getEntityByUsername(targetUsername)
    if (!target) return false
    const { insects } = get()
    if (!insects[userId]) return false
    const insect = insects[userId]
    const nuzzleX = target.entity.position.x + (Math.random() * 4 - 2)
    const nuzzleY = !insect.canFly
      ? (5 + Math.random() * 10)
      : (target.type === 'plant' ? 22 : target.entity.position.y)
    set(state => ({
      insects: {
        ...state.insects,
        [userId]: {
          ...state.insects[userId],
          position: { x: nuzzleX, y: nuzzleY },
          targetX: nuzzleX,
          targetY: nuzzleY,
          isNuzzling: true,
          isFlying: false,
          isWalking: false
        }
      }
    }))
    setTimeout(() => {
      set(state => {
        if (!state.insects[userId]) return state
        return { insects: { ...state.insects, [userId]: { ...state.insects[userId], isNuzzling: false } } }
      })
    }, 3000)
    return true
  },

  stingEntity: (userId, targetUsername) => {
    const target = get().getEntityByUsername(targetUsername)
    if (!target) return false
    const { insects } = get()
    if (!insects[userId]) return false

    // Move bee to the target
    const stingX = target.entity.position.x + (Math.random() * 2 - 1)
    const stingY = target.type === 'plant' ? 22 : target.entity.position.y

    set(state => ({
      insects: {
        ...state.insects,
        [userId]: {
          ...state.insects[userId],
          position: { x: stingX, y: stingY },
          targetX: stingX,
          targetY: stingY,
          isStinging: true,
          isFlying: false,
          isWalking: false
        }
      }
    }))

    // Make target shake from the sting
    const targetUserId = Object.entries(get().plants).find(
      ([, p]) => p.username.toLowerCase() === targetUsername.toLowerCase()
    )?.[0] || Object.entries(get().insects).find(
      ([, i]) => i.username.toLowerCase() === targetUsername.toLowerCase()
    )?.[0]

    if (targetUserId) {
      if (get().plants[targetUserId]) {
        set(state => ({
          plants: { ...state.plants, [targetUserId]: { ...state.plants[targetUserId], isShaking: true } }
        }))
        setTimeout(() => {
          set(state => {
            if (!state.plants[targetUserId]) return state
            return { plants: { ...state.plants, [targetUserId]: { ...state.plants[targetUserId], isShaking: false } } }
          })
        }, 1500)
      } else if (get().insects[targetUserId]) {
        set(state => ({
          insects: { ...state.insects, [targetUserId]: { ...state.insects[targetUserId], isShaking: true } }
        }))
        setTimeout(() => {
          set(state => {
            if (!state.insects[targetUserId]) return state
            return { insects: { ...state.insects, [targetUserId]: { ...state.insects[targetUserId], isShaking: false } } }
          })
        }, 1500)
      }
    }

    // Clear sting animation after 2s
    setTimeout(() => {
      set(state => {
        if (!state.insects[userId]) return state
        return { insects: { ...state.insects, [userId]: { ...state.insects[userId], isStinging: false } } }
      })
    }, 2000)

    return true
  },

  setInsectHat: (userId, hatType) => {
    const { insects } = get()
    if (!insects[userId]) return

    // Clear any existing hat timer
    if (insects[userId]._hatTimer) {
      clearTimeout(insects[userId]._hatTimer)
    }

    if (hatType) {
      // Set hat with 24-hour auto-remove
      const timer = setTimeout(() => {
        set(state => {
          if (!state.insects[userId]) return state
          return {
            insects: {
              ...state.insects,
              [userId]: { ...state.insects[userId], hat: null, _hatTimer: null }
            }
          }
        })
      }, 24 * 60 * 60 * 1000) // 24 hours

      set(state => ({
        insects: {
          ...state.insects,
          [userId]: { ...state.insects[userId], hat: hatType, _hatTimer: timer }
        }
      }))
    } else {
      set(state => ({
        insects: {
          ...state.insects,
          [userId]: { ...state.insects[userId], hat: null, _hatTimer: null }
        }
      }))
    }
  },

  shakePlant: (userId) => {
    const { plants } = get()
    if (!plants[userId]) return
    set(state => ({
      plants: { ...state.plants, [userId]: { ...state.plants[userId], isShaking: true } }
    }))
    setTimeout(() => {
      set(state => {
        if (!state.plants[userId]) return state
        return { plants: { ...state.plants, [userId]: { ...state.plants[userId], isShaking: false } } }
      })
    }, 2000)
  },


  // ============================================
  // COSMETIC VARIANTS
  // ============================================

  setCustomName: (userId, customName) => {
    const { plants } = get()
    if (plants[userId]) {
      set(state => ({
        plants: { ...state.plants, [userId]: { ...state.plants[userId], customName } }
      }))
    }
  },

  setPlantType: async (userId, plantType) => {
    const { plants } = get()
    if (!plants[userId]) return
    set(state => ({
      plants: { ...state.plants, [userId]: { ...state.plants[userId], plantType } }
    }))
    // Persist to server
    await terrariumApi.setEntityType(userId, 'plant', plantType)
  },

  setInsectType: async (userId, insectType) => {
    const { insects } = get()
    if (!insects[userId]) return
    set(state => ({
      insects: {
        ...state.insects,
        [userId]: {
          ...state.insects[userId],
          insectType,
          canFly: FLYING_INSECTS.includes(insectType)
        }
      }
    }))
    // Persist to server
    await terrariumApi.setEntityType(userId, 'insect', insectType)
  },

  setEntityVariant: async (userId, variant) => {
    const { plants, insects } = get()
    if (plants[userId]) {
      set(state => ({
        plants: { ...state.plants, [userId]: { ...state.plants[userId], variant } }
      }))
      await terrariumApi.setEntityVariant(userId, 'plant', variant)
    } else if (insects[userId]) {
      set(state => ({
        insects: { ...state.insects, [userId]: { ...state.insects[userId], variant } }
      }))
      await terrariumApi.setEntityVariant(userId, 'insect', variant)
    }
  },

  // ============================================
  // CHAT BUBBLES
  // ============================================

  showChatBubble: (userId, message) => {
    const { plants, insects } = get()
    const truncated = message.slice(0, 80)

    if (plants[userId]) {
      set(state => ({
        plants: {
          ...state.plants,
          [userId]: { ...state.plants[userId], chatMessage: truncated }
        }
      }))
      setTimeout(() => {
        set(state => {
          if (!state.plants[userId]) return state
          return {
            plants: {
              ...state.plants,
              [userId]: { ...state.plants[userId], chatMessage: null }
            }
          }
        })
      }, 5000)
    } else if (insects[userId]) {
      set(state => ({
        insects: {
          ...state.insects,
          [userId]: { ...state.insects[userId], chatMessage: truncated }
        }
      }))
      setTimeout(() => {
        set(state => {
          if (!state.insects[userId]) return state
          return {
            insects: {
              ...state.insects,
              [userId]: { ...state.insects[userId], chatMessage: null }
            }
          }
        })
      }, 5000)
    }
  },

  // ============================================
  // ROAMING SYSTEM (insects only)
  // ============================================

  startRoaming: () => {
    const { roamingInterval, updateInterval } = get()
    const settings = getSettings()

    if (roamingInterval) clearInterval(roamingInterval)
    if (updateInterval) clearInterval(updateInterval)

    const roamInt = setInterval(() => {
      get().pickNewTargets()
    }, settings.roamInterval || 8000)

    const updateInt = setInterval(() => {
      get().updatePositions()
    }, settings.updateInterval || 200)

    set({ roamingInterval: roamInt, updateInterval: updateInt })
  },

  stopRoaming: () => {
    const { roamingInterval, updateInterval } = get()
    if (roamingInterval) clearInterval(roamingInterval)
    if (updateInterval) clearInterval(updateInterval)
    set({ roamingInterval: null, updateInterval: null })
  },

  pickNewTargets: () => {
    const { insects } = get()
    const settings = getSettings()
    const minX = settings.minX || 5
    const maxX = settings.maxX || 95

    const updates = {}
    for (const [userId, insect] of Object.entries(insects)) {
      // Skip insects that are busy with any action
      if (insect.despawning || insect.isLanded || insect.isVisiting ||
          insect.isDancing || insect.isPetted || insect.isSinging || insect.isNuzzling) continue

      const isGround = !insect.canFly
      const minY = isGround ? 5 : 25
      const maxY = isGround ? 15 : 55

      updates[userId] = {
        ...insect,
        targetX: minX + Math.random() * (maxX - minX),
        targetY: minY + Math.random() * (maxY - minY)
      }
    }

    if (Object.keys(updates).length > 0) {
      set(state => ({
        insects: { ...state.insects, ...updates }
      }))
    }
  },

  updatePositions: () => {
    const { insects } = get()
    const settings = getSettings()
    const speed = settings.roamSpeed || 0.3

    const updates = {}
    let changed = false

    for (const [userId, insect] of Object.entries(insects)) {
      // Skip insects that are busy with any action
      if (insect.despawning || insect.isLanded || insect.spawning ||
          insect.isDancing || insect.isPetted || insect.isSinging || insect.isNuzzling) continue

      const dx = insect.targetX - insect.position.x
      const dy = insect.targetY - insect.position.y
      const distance = Math.sqrt(dx * dx + dy * dy)

      if (distance < 0.5) {
        if (insect.isWalking || insect.isFlying) {
          updates[userId] = { ...insect, isWalking: false, isFlying: false }
          changed = true
        }
        continue
      }

      const moveX = (dx / distance) * speed
      const moveY = (dy / distance) * speed

      const newX = insect.position.x + moveX
      let newY = insect.position.y + moveY

      // Clamp ground insects to ground level
      if (!insect.canFly) {
        newY = Math.min(newY, 15)
      }

      updates[userId] = {
        ...insect,
        position: { x: newX, y: newY },
        facingLeft: dx < 0,
        isWalking: !insect.canFly,
        isFlying: insect.canFly
      }
      changed = true
    }

    if (changed) {
      set(state => ({
        insects: { ...state.insects, ...updates }
      }))
    }
  },

  // ============================================
  // GROWTH POLLING
  // ============================================

  startRefreshPolling: () => {
    const { refreshInterval } = get()
    const settings = getSettings()

    if (refreshInterval) clearInterval(refreshInterval)

    const interval = setInterval(() => {
      get().refreshEntities()
    }, settings.growthUpdateInterval || 15000)

    set({ refreshInterval: interval })
  },

  refreshEntities: async () => {
    const settings = getSettings()

    // Trigger server-side growth calculation
    await terrariumApi.growPlants(
      settings.growthRate || 1,
      settings.healthDecayRate || 2,
      settings.waterDurationMinutes || 30
    )

    // Fetch ALL entities from server (plants + insects)
    const { plants: plantList, insects: insectList } = await terrariumApi.loadAll()

    set(state => {
      // --- Sync plants ---
      const updatedPlants = { ...state.plants }
      const serverPlantIds = new Set(plantList.map(p => p.userId))

      // Update existing plants and add new ones
      const tempPlants = { ...updatedPlants }
      for (const p of plantList) {
        if (updatedPlants[p.userId]) {
          // Merge server data without resetting visual state
          updatedPlants[p.userId] = {
            ...updatedPlants[p.userId],
            growthStage: p.growthStage,
            health: p.health,
            lastWateredAt: p.lastWateredAt,
            isAlive: p.isAlive !== false,
            plantType: p.plantType,
            variant: p.variant || 'default'
          }
        } else {
          // New plant from another client — add it
          const pos = assignSoilPosition(tempPlants)
          updatedPlants[p.userId] = {
            userId: p.userId,
            username: p.username,
            plantType: p.plantType,
            growthStage: p.growthStage,
            health: p.health,
            plantedAt: p.plantedAt,
            lastWateredAt: p.lastWateredAt,
            isAlive: p.isAlive !== false,
            position: { x: parseFloat(p.positionX) || pos.x, y: 0 },
            variant: p.variant || 'default',
            isWatering: false,
            isBlooming: false,
            isWaving: false,
            isShaking: false,
            isGlowing: false,
            spawning: false,
            despawning: false,
            chatMessage: null
          }
          tempPlants[p.userId] = updatedPlants[p.userId]
        }
      }

      // Remove plants that no longer exist on server
      for (const userId of Object.keys(updatedPlants)) {
        if (!serverPlantIds.has(userId)) {
          delete updatedPlants[userId]
        }
      }

      // --- Sync insects ---
      const updatedInsects = { ...state.insects }
      const serverInsectIds = new Set(insectList.map(i => i.userId))

      // Add new insects from server and merge variant/type updates for existing ones
      const tempInsects = { ...updatedInsects }
      for (const i of insectList) {
        if (updatedInsects[i.userId]) {
          // Merge variant/type from server without resetting visual state
          updatedInsects[i.userId] = {
            ...updatedInsects[i.userId],
            insectType: i.insectType,
            canFly: FLYING_INSECTS.includes(i.insectType),
            variant: i.variant || 'default'
          }
        } else {
          // New insect from another client — add it
          const pos = findInsectPosition(tempInsects, i.insectType)
          updatedInsects[i.userId] = {
            userId: i.userId,
            username: i.username,
            insectType: i.insectType,
            canFly: FLYING_INSECTS.includes(i.insectType),
            position: pos,
            targetX: pos.x,
            targetY: pos.y,
            facingLeft: Math.random() > 0.5,
            variant: i.variant || 'default',
            isWalking: false,
            isFlying: false,
            isLanded: false,
            landedOnUserId: null,
            isPollinatingUserId: null,
            isVisiting: false,
            isDancing: false,
            isPetted: false,
            isSinging: false,
            isNuzzling: false,
            chatMessage: null,
            spawning: false,
            despawning: false,
            lastActivity: Date.now()
          }
          tempInsects[i.userId] = updatedInsects[i.userId]
        }
      }

      // Remove insects that no longer exist on server
      for (const userId of Object.keys(updatedInsects)) {
        if (!serverInsectIds.has(userId)) {
          delete updatedInsects[userId]
        }
      }

      return { plants: updatedPlants, insects: updatedInsects }
    })

    // Ensure roaming is started if insects were added
    const { insects, roamInterval } = get()
    if (Object.keys(insects).length > 0 && !roamInterval) {
      get().startRoaming()
    }
  },

  // ============================================
  // ADMIN BULK ACTIONS
  // ============================================

  waterAllPlants: async (actorUserId, actorUsername) => {
    const { plants } = get()
    for (const userId of Object.keys(plants)) {
      await get().waterPlant(userId, actorUserId, actorUsername)
    }
  },

  clearAll: async () => {
    get().stopRoaming()
    const { refreshInterval } = get()
    if (refreshInterval) clearInterval(refreshInterval)

    await Promise.all([
      terrariumApi.removeAllPlants(),
      terrariumApi.removeAllInsects()
    ])

    set({
      plants: {},
      insects: {},
      refreshInterval: null,
      lastAction: null
    })
  },

  removeEntityByUsername: async (username) => {
    const plant = get().getPlantByUsername(username)
    if (plant) {
      await get().removePlant(plant.userId)
      return true
    }
    const insect = get().getInsectByUsername(username)
    if (insect) {
      await get().removeInsect(insect.userId)
      return true
    }
    return false
  }
}))

export default useTerrariumStore